/* global PublicKeyCredential */
const VAULT_PREFIX = 'genesis_vault_';
const PBKDF2_ITERATIONS = 600000;
const enc = new TextEncoder();

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const vaultKey = (address) => `${VAULT_PREFIX}${address}`;

// ---------- helpers ----------
async function deriveKEK(password, salt, iterations) {
  const base = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

const importDEK = (raw) => crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);

async function aesEncrypt(keyObj, data) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, keyObj, data);
  return { iv: b64(iv), ct: b64(ct) };
}

async function aesDecrypt(keyObj, { iv, ct }) {
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(iv) }, keyObj, unb64(ct)));
}

async function keypairFrom(secret) {
  const { Keypair } = await import('@solana/web3.js');
  return Keypair.fromSecretKey(secret);
}

// ---------- public API ----------

export const hasVault = (address) => {
  try {
    return !!address && !!localStorage.getItem(vaultKey(address));
  } catch {
    return false;
  }
};

export const validatePassword = (pw) => {
  if (!pw || pw.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) return 'Password must contain letters and digits';
  return null;
};

// Приймає повний текст .txt-бекапу Genesis або просто масив "[1,2,...]" (64 числа)
export function parseSecretKey(input) {
  const m = String(input).match(/\[\s*\d+(\s*,\s*\d+){63}\s*\]/);
  if (!m) throw new Error('Secret key not found. Paste the full backup text or the [ ... ] array.');
  const arr = JSON.parse(m[0]);
  if (arr.length !== 64 || arr.some((n) => n < 0 || n > 255)) throw new Error('Invalid secret key');
  return Uint8Array.from(arr);
}

// Створює зашифроване сховище. Перевіряє: ключ відповідає адресі профілю + тестове розшифрування.
export async function createVault(secretInput, password, expectedAddress) {
  const pwError = validatePassword(password);
  if (pwError) throw new Error(pwError);

  const secret = parseSecretKey(secretInput);
  const kp = await keypairFrom(secret);
  const address = kp.publicKey.toBase58();

  if (expectedAddress && address !== expectedAddress) {
    throw new Error('This key does not match your Genesis Solana address');
  }
  if (hasVault(address)) throw new Error('Wallet is already protected on this device');

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const dekRaw = crypto.getRandomValues(new Uint8Array(32));
  const dek = await importDEK(dekRaw);
  const kek = await deriveKEK(password, salt, PBKDF2_ITERATIONS);

  const vault = {
    v: 1,
    address,
    kdf: { name: 'PBKDF2-SHA256', iterations: PBKDF2_ITERATIONS, salt: b64(salt) },
    secret: await aesEncrypt(dek, secret),
    unlocks: { password: await aesEncrypt(kek, dekRaw) },
    createdAt: new Date().toISOString(),
  };

  localStorage.setItem(vaultKey(address), JSON.stringify(vault));

  // Тестове розшифрування: якщо не збіглось — прибираємо запис, відкритий ключ лишається
  try {
    const check = await unlockVault(address, password);
    if (check.publicKey.toBase58() !== address) throw new Error('mismatch');
  } catch {
    localStorage.removeItem(vaultKey(address));
    throw new Error('Vault verification failed. Nothing was changed.');
  }
  return address;
}

// Повертає Keypair (для Send/Swap). Невірний пароль -> 'Wrong password'
export async function unlockVault(address, password) {
  const raw = localStorage.getItem(vaultKey(address));
  if (!raw) throw new Error('No protected wallet on this device');
  const vault = JSON.parse(raw);

  const kek = await deriveKEK(password, unb64(vault.kdf.salt), vault.kdf.iterations);
  let dekRaw;
  try {
    dekRaw = await aesDecrypt(kek, vault.unlocks.password);
  } catch {
    throw new Error('Wrong password');
  }
  const secret = await aesDecrypt(await importDEK(dekRaw), vault.secret);
  return keypairFrom(secret);
}

// Завантажує .txt-бекап у тому ж форматі, що й generateSolanaWallet
export async function exportBackup(address, password) {
  const kp = await unlockVault(address, password);
  const secretKey = JSON.stringify(Array.from(kp.secretKey));
  const data = `Genesis Solana Wallet Backup\nAddress: ${address}\nSecret Key: ${secretKey}\nKEEP THIS SAFE!`;
  const blob = new Blob([data], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `genesis-solana-${address.slice(0, 8)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// ===== FACE ID / FINGERPRINT (WebAuthn Passkey + PRF) =====
const PASSKEY_INFO = enc.encode('genesis-wallet-passkey-v1');

const readVault = (address) => {
  const raw = localStorage.getItem(vaultKey(address));
  if (!raw) throw new Error('No protected wallet on this device');
  return JSON.parse(raw);
};

// PRF output (32 байти) -> AES-ключ через HKDF
async function prfToKEK(prfOutput) {
  const base = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: PASSKEY_INFO },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function passkeyError(e) {
  if (e && e.name === 'NotAllowedError') return new Error('Face ID / fingerprint was cancelled or timed out');
  if (e && e.name === 'InvalidStateError') return new Error('A passkey for this wallet already exists on this device');
  return e instanceof Error ? e : new Error(String(e));
}

// Запит PRF-секрету в існуючого passkey (показує Face ID / відбиток)
async function getPrfOutput(credentialIdB64, prfSaltB64) {
  let assertion;
  try {
    assertion = await navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [{ type: 'public-key', id: unb64(credentialIdB64) }],
        userVerification: 'required',
        timeout: 60000,
        extensions: { prf: { eval: { first: unb64(prfSaltB64) } } },
      },
    });
  } catch (e) {
    throw passkeyError(e);
  }
  const out = assertion?.getClientExtensionResults?.().prf?.results?.first;
  if (!out) throw new Error('This device does not support Face ID unlock for the wallet');
  return new Uint8Array(out);
}

// Чи може цей пристрій узагалі мати Face ID / відбиток для сайту
export async function isPasskeySupported() {
  try {
    if (!window.PublicKeyCredential || !navigator.credentials) return false;
    if (PublicKeyCredential.getClientCapabilities) {
      const caps = await PublicKeyCredential.getClientCapabilities();
      if (caps && caps['extension:prf'] === false) return false;
    }
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export const hasPasskey = (address) => {
  try {
    const raw = localStorage.getItem(vaultKey(address));
    return !!raw && !!JSON.parse(raw).unlocks?.passkey;
  } catch {
    return false;
  }
};

// Увімкнути, крок 1: Face ID запускається ПЕРШИМ (Safari вимагає це одразу після натискання).
// Пароль перевіряється в інтерфейсі ДО цього кроку (unlockVault).
// Повертає 'enabled' або 'pending' (iPhone: потрібне друге підтвердження — finishPasskey).
export async function enablePasskey(address, password) {
  const vault = readVault(address);
  if (vault.unlocks?.passkey) throw new Error('Face ID is already enabled on this device');

  const prfSalt = crypto.getRandomValues(new Uint8Array(32));
  let cred;
  try {
    cred = await navigator.credentials.create({
      publicKey: {
        rp: { name: 'Genesis by THEO' },
        user: {
          id: crypto.getRandomValues(new Uint8Array(16)),
          name: `Genesis wallet ${address.slice(0, 4)}…${address.slice(-4)}`,
          displayName: 'Genesis Wallet',
        },
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey: 'preferred',
          userVerification: 'required',
        },
        timeout: 60000,
        extensions: { prf: { eval: { first: prfSalt } } },
      },
    });
  } catch (e) {
    throw passkeyError(e);
  }

  const ext = cred?.getClientExtensionResults?.() || {};
  if (!ext.prf || ext.prf.enabled === false) {
    throw new Error('This device or browser does not support Face ID unlock. Keep using your password.');
  }
  const credentialId = b64(cred.rawId);

  // Chrome / Android: секрет приходить одразу
  if (ext.prf.results?.first) {
    await sealPasskey(address, password, credentialId, b64(prfSalt), new Uint8Array(ext.prf.results.first));
    return 'enabled';
  }

  // Safari / iPhone: секрет прийде лише після другого Face ID (окремим натисканням)
  vault.unlocks.passkeyPending = { credentialId, prfSalt: b64(prfSalt) };
  localStorage.setItem(vaultKey(address), JSON.stringify(vault));
  return 'pending';
}

// Увімкнути, крок 2 (лише iPhone/Safari): друге підтвердження Face ID
export async function finishPasskey(address, password) {
  const pending = readVault(address).unlocks?.passkeyPending;
  if (!pending) throw new Error('Nothing to finish. Start Face ID setup again.');
  const prfOut = await getPrfOutput(pending.credentialId, pending.prfSalt); // Face ID — першим ділом
  await sealPasskey(address, password, pending.credentialId, pending.prfSalt, prfOut);
  return 'enabled';
}

// Пароль -> майстер-ключ -> шифруємо його секретом Face ID -> перевіряємо -> зберігаємо
async function sealPasskey(address, password, credentialId, prfSaltB64, prfOut) {
  const vault = readVault(address);
  const kekPw = await deriveKEK(password, unb64(vault.kdf.salt), vault.kdf.iterations);
  let dekRaw;
  try {
    dekRaw = await aesDecrypt(kekPw, vault.unlocks.password);
  } catch {
    throw new Error('Wrong password');
  }

  const kekPk = await prfToKEK(prfOut);
  const sealed = await aesEncrypt(kekPk, dekRaw);
  const entry = { credentialId, prfSalt: prfSaltB64, ...sealed, createdAt: new Date().toISOString() };

  const check = await aesDecrypt(kekPk, entry);
  if (b64(check) !== b64(dekRaw)) throw new Error('Face ID setup verification failed. Nothing was changed.');

  vault.unlocks.passkey = entry;
  delete vault.unlocks.passkeyPending;
  localStorage.setItem(vaultKey(address), JSON.stringify(vault));
}
// Відкрити гаманець через Face ID / відбиток -> Keypair (як unlockVault)
export async function unlockWithPasskey(address) {
  const vault = readVault(address);
  const pk = vault.unlocks?.passkey;
  if (!pk) throw new Error('Face ID is not enabled on this device');

  const prfOut = await getPrfOutput(pk.credentialId, pk.prfSalt);
  const kek = await prfToKEK(prfOut);
  let dekRaw;
  try {
    dekRaw = await aesDecrypt(kek, pk);
  } catch {
    throw new Error('Face ID unlock failed. Use your password.');
  }
  const secret = await aesDecrypt(await importDEK(dekRaw), vault.secret);
  const kp = await keypairFrom(secret);
  if (kp.publicKey.toBase58() !== address) throw new Error('Wallet key mismatch');
  return kp;
}

// Вимкнути Face ID для гаманця на цьому пристрої (пароль і ключ не чіпаються)
export function disablePasskey(address) {
  const vault = readVault(address);
  if (vault.unlocks?.passkey || vault.unlocks?.passkeyPending) {
    delete vault.unlocks.passkey;
    delete vault.unlocks.passkeyPending;
    localStorage.setItem(vaultKey(address), JSON.stringify(vault));
  }
}
// ===== END FACE ID / FINGERPRINT =====